import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Clock, Plus, Trash2, CalendarX, CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatTime12Hour, cn } from '@/lib/utils';
import { format } from 'date-fns';

interface TimeBlock {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  block_name: string;
  is_active: boolean;
}

interface BlackoutDate {
  id?: string;
  blackout_date: string;
  reason?: string;
}

interface AdvancedAvailabilityManagerProps {
  mentorId: string;
  isAdmin?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const AdvancedAvailabilityManager: React.FC<AdvancedAvailabilityManagerProps> = ({ 
  mentorId, 
  isAdmin = false 
}) => {
  const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<BlackoutDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBlackoutDate, setNewBlackoutDate] = useState<Date>();
  const [newBlackoutReason, setNewBlackoutReason] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [mentorId]);

  const fetchData = async () => {
    try {
      // Fetch time blocks
      const { data: blocksData, error: blocksError } = await supabase
        .from('mentor_availability')
        .select('*')
        .eq('mentor_id', mentorId)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (blocksError) throw blocksError;

      // Fetch blackout dates
      const { data: blackoutData, error: blackoutError } = await supabase
        .from('mentor_blackout_dates')
        .select('*')
        .eq('mentor_id', mentorId)
        .order('blackout_date', { ascending: true });

      if (blackoutError) throw blackoutError;

      setTimeBlocks(blocksData || []);
      setBlackoutDates(blackoutData || []);
    } catch (error: any) {
      console.error('Error fetching availability data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load availability data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const addTimeBlock = (dayOfWeek: number) => {
    const newBlock: TimeBlock = {
      day_of_week: dayOfWeek,
      start_time: '09:00',
      end_time: '12:00',
      block_name: 'Block ' + (timeBlocks.filter(b => b.day_of_week === dayOfWeek).length + 1),
      is_active: true,
    };
    setTimeBlocks(prev => [...prev, newBlock]);
  };

  const updateTimeBlock = (index: number, field: keyof TimeBlock, value: string | boolean) => {
    setTimeBlocks(prev => prev.map((block, i) => 
      i === index ? { ...block, [field]: value } : block
    ));
  };

  const removeTimeBlock = (index: number) => {
    setTimeBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const saveTimeBlocks = async () => {
    try {
      // Delete all existing blocks for this mentor
      await supabase
        .from('mentor_availability')
        .delete()
        .eq('mentor_id', mentorId);

      // Insert active blocks
      const activeBlocks = timeBlocks.filter(block => block.is_active);
      if (activeBlocks.length > 0) {
        const { error } = await supabase
          .from('mentor_availability')
          .insert(activeBlocks.map(block => ({
            mentor_id: mentorId,
            day_of_week: block.day_of_week,
            start_time: block.start_time,
            end_time: block.end_time,
            block_name: block.block_name,
            is_active: block.is_active,
          })));

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: 'Time blocks saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving time blocks:', error);
      toast({
        title: 'Error',
        description: 'Failed to save time blocks',
        variant: 'destructive',
      });
    }
  };

  const addBlackoutDate = async () => {
    if (!newBlackoutDate) {
      toast({
        title: 'Error',
        description: 'Please select a date',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Format date in local timezone to prevent off-by-one errors
      const year = newBlackoutDate.getFullYear();
      const month = String(newBlackoutDate.getMonth() + 1).padStart(2, '0');
      const day = String(newBlackoutDate.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;

      console.log('Adding blackout date:', { 
        originalDate: newBlackoutDate,
        localDateString,
        mentorId
      });

      const { data, error } = await supabase
        .from('mentor_blackout_dates')
        .insert([{
          mentor_id: mentorId,
          blackout_date: localDateString,
          reason: newBlackoutReason || null,
        }])
        .select()
        .single();

      if (error) throw error;

      setBlackoutDates(prev => [...prev, data]);
      setNewBlackoutDate(undefined);
      setNewBlackoutReason('');

      toast({
        title: 'Success',
        description: 'Blackout date added successfully',
      });
    } catch (error: any) {
      console.error('Error adding blackout date:', error);
      toast({
        title: 'Error',
        description: 'Failed to add blackout date',
        variant: 'destructive',
      });
    }
  };

  const removeBlackoutDate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('mentor_blackout_dates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setBlackoutDates(prev => prev.filter(date => date.id !== id));

      toast({
        title: 'Success',
        description: 'Blackout date removed successfully',
      });
    } catch (error: any) {
      console.error('Error removing blackout date:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove blackout date',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading availability...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Blocks Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            When Can Students Book You?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-primary/10 border-2 border-primary/20 rounded-lg">
            <p className="font-semibold text-primary mb-1">✓ These are the times when you ARE available for bookings</p>
            <p className="text-sm text-muted-foreground">
              Set the specific hours when students can book sessions with you. You can create multiple time blocks per day for breaks or split schedules.
            </p>
          </div>
          
          {DAYS_OF_WEEK.map((day) => {
            const dayBlocks = timeBlocks.filter(block => block.day_of_week === day.value);
            
            return (
                  <div key={day.value} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{day.label}</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addTimeBlock(day.value)}
                    className="flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Available Time
                  </Button>
                </div>
                
                {dayBlocks.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4 border-2 border-dashed rounded-lg">
                    Not accepting bookings on {day.label}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayBlocks.map((block, blockIndex) => {
                      const globalIndex = timeBlocks.findIndex(b => b === block);
                      return (
                        <div key={globalIndex} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center p-4 border rounded-lg">
                          <div>
                            <Label htmlFor={`block-name-${globalIndex}`} className="text-sm">Block Name</Label>
                            <Input
                              id={`block-name-${globalIndex}`}
                              value={block.block_name}
                              onChange={(e) => updateTimeBlock(globalIndex, 'block_name', e.target.value)}
                              placeholder="e.g., Morning Hours"
                              className="bg-background"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor={`start-${globalIndex}`} className="text-sm">Start Time</Label>
                            <Input
                              id={`start-${globalIndex}`}
                              type="time"
                              value={block.start_time}
                              onChange={(e) => updateTimeBlock(globalIndex, 'start_time', e.target.value)}
                              className="bg-background"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor={`end-${globalIndex}`} className="text-sm">End Time</Label>
                            <Input
                              id={`end-${globalIndex}`}
                              type="time"
                              value={block.end_time}
                              onChange={(e) => updateTimeBlock(globalIndex, 'end_time', e.target.value)}
                              className="bg-background"
                            />
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`active-${globalIndex}`}
                              checked={block.is_active}
                              onChange={(e) => updateTimeBlock(globalIndex, 'is_active', e.target.checked)}
                              className="rounded"
                            />
                            <Label htmlFor={`active-${globalIndex}`} className="text-sm">
                              Active
                            </Label>
                          </div>
                          
                          <div className="text-sm text-muted-foreground">
                            {block.is_active 
                              ? `${formatTime12Hour(block.start_time)} - ${formatTime12Hour(block.end_time)}`
                              : 'Inactive'
                            }
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTimeBlock(globalIndex)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {day.value < DAYS_OF_WEEK.length - 1 && <Separator />}
              </div>
            );
          })}
          
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={saveTimeBlocks} variant="default" size="lg">
              Save Available Times
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blackout Dates Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CalendarX className="w-5 h-5 mr-2" />
            Blackout Dates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-destructive/10 border-2 border-destructive/20 rounded-lg">
            <p className="font-semibold text-destructive mb-1">✗ These are dates when you are NOT available</p>
            <p className="text-sm text-muted-foreground">
              Use blackout dates to block specific days when you cannot take bookings (vacations, holidays, etc.).
            </p>
          </div>
          
          {/* Add Blackout Date */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <h4 className="font-medium">Add Blackout Date</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="blackout-date" className="text-sm">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !newBlackoutDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newBlackoutDate ? format(newBlackoutDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newBlackoutDate}
                      onSelect={setNewBlackoutDate}
                      initialFocus
                      disabled={(date) => date < new Date()}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label htmlFor="blackout-reason" className="text-sm">Reason (Optional)</Label>
                <Input
                  id="blackout-reason"
                  value={newBlackoutReason}
                  onChange={(e) => setNewBlackoutReason(e.target.value)}
                  placeholder="e.g., Vacation, Holiday"
                  className="bg-background"
                />
              </div>
              
              <div className="flex items-end">
                <Button onClick={addBlackoutDate} className="w-full">
                  Add Blackout Date
                </Button>
              </div>
            </div>
          </div>
          
          {/* Existing Blackout Dates */}
          <div className="space-y-2">
            <h4 className="font-medium">Current Blackout Dates</h4>
            {blackoutDates.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4 border-2 border-dashed rounded-lg">
                No blackout dates set
              </div>
            ) : (
              <div className="space-y-2">
                {blackoutDates.map((blackoutDate) => (
                  <div key={blackoutDate.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                       <div className="font-medium">
                        {format(new Date(blackoutDate.blackout_date + 'T00:00:00'), 'PPP')}
                      </div>
                      {blackoutDate.reason && (
                        <div className="text-sm text-muted-foreground">
                          {blackoutDate.reason}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBlackoutDate(blackoutDate.id!)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAvailabilityManager;
